import { getDescription } from '@/containers/Order/utils/getFailInfo'
import React, { useCallback } from 'react'
import { type TFunction, useTranslation } from 'react-i18next'

interface InfoType {
  /** 失败原因枚举 */
  failReason: string
  failData: {
    action?: string
    status?: string
    mainStatus?: string
    msg?: string
  }
}
function normalErrors(t: TFunction, i: InfoType) {
  const { failReason } = i
  // 优先级 enums.outboundFailReason.xxx > system.error.xxx > failReason(如果是LST-开头)
  let faimMsg = ''
  if (failReason) {
    faimMsg = t(`enums.outboundFailReason.${failReason}`, '') || t(`system.error.${failReason}`, '') || (failReason.startsWith('LST-') ? t(failReason, '') : '')
  }

  const list = [faimMsg, getDescription(i, t)]

  return list.filter(Boolean).join(', ')
}

/** 获取出库错误原因信息 */
export function useOutboundFailInfo() {
  const { t: originT } = useTranslation()

  const t = useCallback(
    (i18nKey: string, option = {}) => {
      const translate = originT([i18nKey, ''], option)
      if (!translate) return
      return translate
    },
    [originT]
  )

  /**
   * 后端failReason走通用逻辑的情况下，错误提示不明确，需要前端自己处理 引入failAction做动作区分
   */
  const formatOutboundFailInfos = useCallback(
    (outboundFailInfos: InfoType[], msg = '', failAction?) => {
      const failInfos = outboundFailInfos?.map((it, index) => {
        // 兼容没有failData场景
        const { action, status, mainStatus } = it.failData || {}
        const statusEnum = {
          pending: t('order.status.pending'),
          to_outbound: t('order.status.toOutbound'),
          completed: t('order.status.completed'),
          cancelled: t('order.status.cancelled'),
        }

        const actionMap = {
          order_batch_add_sku: 'add',
          order_batch_update_sku: 'update',
          order_batch_delete_sku: 'delete',
        }
        if (it.failReason === 'status_not_support') {
          /**批量修改订单商品 后端走了通用逻辑 配合failAction判断 */
          if (Object.keys(actionMap).includes(failAction)) {
            if (['partially_blocking', 'allocated'].includes(status)) {
              return t('enums.outboundFailReason.order_occupied_stock')
            }
            return t('enums.outboundFailReason.updateOrderGoodError.status', { status: statusEnum[mainStatus], action: t(`order.goods.${actionMap[failAction]}`) })
          }
          if (action === 'platform_order_update') {
            // 只有待处理/已作废/部分预占库存 订单支持更新订单数据
            const isSurppot = ['pending', 'cancelled', 'partially_blocking'].includes(status)
            if (!isSurppot) {
              return t('enums.outboundFailReason.notSuppotUpdate')
            }
          }
          // 恢复订单-失败信息特殊处理
          if (action === 'recover') {
            return t('enums.outboundFailReason.order_must_cancelled')
          }

          // 更新物流失败
          if (action === 'update') {
            if (mainStatus === 'pending') {
              const tipEnum = {
                partially_blocking: t('enums.outboundFailReason.updateExpressPendingError'),
                allocated: t('enums.outboundFailReason.updateExpressPendingError'),
              }
              return <span key={index}>{tipEnum[status]}</span>
            }
            const statusText = statusEnum[mainStatus]
            return <span key={index}>{t('enums.outboundFailReason.updateWarehouseError', { status: statusText })}</span>
          }

          // 取消合单
          if (action === 'cancel_merge') {
            return t('order.cancelMergeOrderNotSuppo', { status: t(`enums.outboundMainStatus.${mainStatus}`) })
          }

          // 修改仓库
          if (action === 'update_warehouse') {
            return t('order.update_warehouse_not_support')
          }

          /**
           * 目前后端返回的 status 取值有：
           * pending（待处理）、to_outbound（待出库）、completed（已出库）、cancelled（已作废）
           */

          const rs = t(`enums.outboundFailReason.${action}.${status}`)
          if (rs) return rs
          const extra = getExtra(it, originT)

          if (extra) return t(`enums.outboundFailReason.status_not_support_2`, extra)

          // 猛哥说要特殊处理，有问题找猛哥
          const key = `enums.outboundFailReason.status_not_support.${action}`
          // 必须要用原生的originT不能用t，因为t会返回undefined，不会返回key
          if (originT(key) !== key) {
            return t(key)
          } else {
            return t('enums.outboundFailReason.status_not_support')
          }
        }

        if (it?.failReason === 'shopline_label_error') {
          // 调用第三方错误，和枚举值区分
          return (
            <div key={index}>
              {t(`detail.outboundFailReason.shopline_label_error`)}
              {it?.failData?.msg || ''}；
            </div>
          )
        }

        if (it?.failReason === 'order_is_privacy') {
          return t(`enums.recoverOrderFailReason.order_is_privacy`)
        }

        if (it?.failReason?.toLowerCase() === 'duplicate_delivery') {
          // OMS重复发货
          return <div key={index}>{it?.failData?.msg || ''}</div>
        }

        /** 正在批量处理某个任务。后面要新增任务种类的话只要加多语言就行，按照action的那个key去加。 */
        if (it?.failReason === 'batch_task_queueing') {
          return t(`enums.outboundFailReason.batch_task_queueing`, { name: t(`enums.outboundFailReason.batch_task_queueing.${action}`) })
        }
        // 虾皮接口异常
        if (msg === 'LST-133242-B0335' && it.failReason === 'logistics_no_or_waybill_error') {
          return t(`system.error.LST-133242-B0335`)
        }
        //虾皮订单已经发货
        if (it.failReason === 'logistics_no_or_waybill_error' && it?.failData?.action == 'platform_status_not_support') {
          return t(`enums.outboundFailReason.${it?.failData?.action}`, { status: it?.failData?.status })
        }
        //订单状态为{{status}}，无法执行批量修改订单商品
        if (it.failReason === 'order_status_not_batch_sku') {
          return t('enums.outboundFailReason.updateOrderGoodError.status', { status: statusEnum[mainStatus], action: t(`order.goods.${actionMap[action]}`) })
        }
        // 订单不存在sku{{skuId}}，无法执行更换商品
        if (it.failReason === 'order_sku_not_exist') {
          let skuId = ''
          try {
            skuId = JSON.parse(it.failData?.msg)?.skuId
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e)
          }
          return t('enums.outboundFailReason.updateOrderGoodError.orderDelete', { skuId })
        }

        return normalErrors(originT, it)
      })

      const filterEmptyResult = failInfos?.filter(Boolean)
      if (filterEmptyResult?.length === 0) return ''
      return filterEmptyResult
    },
    [originT, t]
  )

  return {
    formatOutboundFailInfos,
  }
}

function getExtra(outboundFailInfo: InfoType, t: TFunction) {
  const { action, mainStatus } = outboundFailInfo.failData || {}
  const extraKeys = [`enums.outboundMainStatus.${mainStatus}`, `task.taskType.order_${action}`]
  if (extraKeys.some((key) => !hasI18nText(key, t))) return null

  return {
    mainStatus: t(extraKeys[0]),
    action: t(extraKeys[1]),
  }
}

function hasI18nText(key: string, t: TFunction) {
  const text = t(key)

  return text !== key && text !== ''
}
